import { type ComponentProps, type FormEvent, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next/pages';
import { GetListingResponseStatus } from '@pkgs/api/index';
import { IconButton } from '@ev-tech/ui-core-components/IconButton';
import { Headline } from '@ev-tech/ui-core-components/Headline';
import { Copy } from '@ev-tech/ui-core-components/Copy';
import { Button } from '@ev-tech/ui-core-components/Button';

import { nextTick } from '@/utils/timeout';
import { extractFormData } from '@/utils/formUtils';
import { TRANSLATION_NAMESPACE } from '@/i18n/config';
import { usePrivateOfficePasswordStore } from '@/hooks/usePrivateOfficePasswordStore';
import { useListing } from '@/hooks/queries/useListing';
import { EyeOffIcon } from '@/components/common/Icons/EyeOffIcon';
import { EyeIcon } from '@/components/common/Icons/EyeIcon';

import { type PasswordFormData } from './PrivateOfficePasswordForm.types';
import { TextInput } from '../TextInput/TextInput';

import styles from './PrivateOfficePasswordForm.module.css';

type PasswordTextInputProps = Omit<ComponentProps<typeof TextInput>, 'type'> & {
  type?: 'text' | 'password';
};

const PasswordTextInput = TextInput as (props: PasswordTextInputProps) => React.JSX.Element;

export const PrivateOfficePasswordForm = ({ isEmbedded = false }: { isEmbedded?: boolean }) => {
  const { t } = useTranslation(TRANSLATION_NAMESPACE.property_expose);
  const { refetch } = useListing();
  const formRef = useRef<HTMLFormElement>(null);
  const setPrivateOfficePassword = usePrivateOfficePasswordStore((state) => state.setPrivateOfficePassword);

  const [controlledError, setControlledError] = useState<string | undefined>(undefined);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const handleSubmit = useCallback(
    async (e?: FormEvent<HTMLFormElement>) => {
      e?.preventDefault();
      // check if form is valid and scroll to first invalid input
      if (!formRef.current?.checkValidity()) {
        const firstInvalidInput = formRef.current?.querySelector<HTMLInputElement>('input:invalid');
        firstInvalidInput?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        firstInvalidInput?.focus({ preventScroll: true });
        return;
      }

      const formData = extractFormData<PasswordFormData>(formRef.current);

      setPrivateOfficePassword(formData.password);

      // wait one tick for the password to be set before refetching the listing
      await nextTick();

      const { data } = await refetch();

      if (data?.status === GetListingResponseStatus.INVALID_PASSWORD) {
        setControlledError(t('search.request.access.form.validation.password.incorrect'));
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    [refetch, t, setPrivateOfficePassword],
  );

  const FormComponent = (
    <form
      className={styles.form}
      data-testid="request-access-form"
      ref={formRef}
      onSubmit={handleSubmit}
      noValidate
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          handleSubmit();
        }
      }}
    >
      <div className={styles.titleWrapper}>
        <Headline className={styles.headline} size="h4" label={t('search.request.access.password.form.title')} />
        <Copy className={styles.subtitleCopy} text={t('search.request.access.password.form.subtitle')} />
      </div>
      <PasswordTextInput
        label={t('search.request.access.form.label.password')}
        name="password"
        type={isPasswordVisible ? 'text' : 'password'}
        isRequired
        onChange={() => setControlledError(undefined)}
        controlledError={controlledError}
        validationMessages={{
          valueMissing: t('search.request.access.form.validation.password.required'),
        }}
        endAdornment={
          <IconButton
            className={styles.iconButton}
            icon={isPasswordVisible ? <EyeOffIcon /> : <EyeIcon />}
            label={
              isPasswordVisible
                ? t('search.request.access.form.button.icon.hidePassword')
                : t('search.request.access.form.button.icon.showPassword')
            }
            onClick={() => setIsPasswordVisible((prev) => !prev)}
            variant="tertiary"
            size="small"
            type="button"
          />
        }
      />
      <Button
        className={styles.button}
        type="submit"
        iconPosition="left"
        label={t('search.request.access.form.button.send.password')}
        testId="search-components_expose-request-access-form_submit-button"
        onClick={(e) => e.stopPropagation()}
      />
    </form>
  );

  return isEmbedded ? <div className={styles.embeddedFormWrapper}>{FormComponent}</div> : FormComponent;
};
